// src/pages/dropshipping/ProviderProductsTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Save } from 'lucide-react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import DataTable from '../../components/common/DataTable/DataTable';
import FilterPanel from '../../components/common/FilterPanel/FilterPanel';
import { getProviderProducts, updateProviderProductDropshipping } from '../../services/api/dropshipping';
import styles from './ProviderProductsTab.module.css';

const ProviderProductsTab = () => {
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    total: 0,
    pageSizeOptions: [10, 20, 50],
  });
  const [sorting, setSorting] = useState({ orderBy: 'productName', orderDirection: 'ASC' });
  const [filters, setFilters] = useState({ dropshippingEnabled: '' });
  const [search, setSearch] = useState('');
  const [pendingEdits, setPendingEdits] = useState({});
  const [savingRows, setSavingRows] = useState({});

  const getRowKey = (row) => `${row.type}_${row.id}`;

  const getRowValue = (row, field) => {
    const key = getRowKey(row);
    if (pendingEdits[key] && pendingEdits[key][field] !== undefined) {
      return pendingEdits[key][field];
    }
    return row[field];
  };

  const handleEdit = (row, field, value) => {
    const key = getRowKey(row);
    setPendingEdits((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {
          dropshippingEnabled: row.dropshippingEnabled,
          dropshippingPrice: row.dropshippingPrice ?? '',
          minimumSellingPrice: row.minimumSellingPrice ?? '',
          maximumSellingPrice: row.maximumSellingPrice ?? '',
        }),
        [field]: value,
      },
    }));
  };

  const handleSave = async (row) => {
    const key = getRowKey(row);
    const edits = pendingEdits[key];
    if (!edits) return;

    setSavingRows((prev) => ({ ...prev, [key]: true }));
    try {
      await updateProviderProductDropshipping(row.id, row.type, edits);
      setData((prev) =>
        prev.map((r) =>
          getRowKey(r) === key
            ? { ...r, ...edits }
            : r
        )
      );
      setPendingEdits((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      success(t('dropshipping.priceUpdated'));
    } catch (err) {
      showError(err.message || t('dropshipping.failedToSave'));
    } finally {
      setSavingRows((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const loadData = useCallback(async (pg = pagination, srt = sorting, flt = filters, srch = search) => {
    setLoading(true);
    try {
      const result = await getProviderProducts({
        page: pg.currentPage,
        pageSize: pg.pageSize,
        search: srch,
        dropshippingEnabled: flt.dropshippingEnabled || undefined,
        orderBy: srt.orderBy,
        orderDirection: srt.orderDirection,
      });
      setData(result.rows);
      setPagination((prev) => ({ ...prev, total: result.total }));
    } catch (err) {
      showError(err.message || t('dropshipping.failedToSave'));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadData(pagination, sorting, filters, search);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    const newPagination = { ...pagination, currentPage: 1 };
    setPagination(newPagination);
    loadData(newPagination, sorting, newFilters, search);
  };

  const handleClearFilters = () => {
    const cleared = { dropshippingEnabled: '' };
    setFilters(cleared);
    const newPagination = { ...pagination, currentPage: 1 };
    setPagination(newPagination);
    loadData(newPagination, sorting, cleared, search);
  };

  const handleSearchChange = (val) => {
    setSearch(val);
    const newPagination = { ...pagination, currentPage: 1 };
    setPagination(newPagination);
    loadData(newPagination, sorting, filters, val);
  };

  const handleSortChange = (newSorting) => {
    setSorting(newSorting);
    loadData(pagination, newSorting, filters, search);
  };

  const handlePaginationChange = (newPagination) => {
    setPagination(newPagination);
    loadData(newPagination, sorting, filters, search);
  };

  const handleExport = async () => {
    try {
      const result = await getProviderProducts({
        page: 1,
        pageSize: 9999,
        search,
        dropshippingEnabled: filters.dropshippingEnabled || undefined,
        orderBy: sorting.orderBy,
        orderDirection: sorting.orderDirection,
      });
      const headers = [
        t('dropshipping.product'),
        t('dropshipping.dropshippingAllowed'),
        t('dropshipping.dropshippingPrice'),
        t('dropshipping.minSellingPrice'),
        t('dropshipping.maxSellingPrice'),
      ];
      const rows = result.rows.map((row) => [
        row.variationLabel ? `${row.productName} (${row.variationLabel})` : row.productName,
        row.dropshippingEnabled ? 'Yes' : 'No',
        row.dropshippingPrice ?? '',
        row.minimumSellingPrice ?? '',
        row.maximumSellingPrice ?? '',
      ]);
      const csv = [headers, ...rows]
        .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
        .join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'dropshipping-products.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      showError(err.message || t('dropshipping.failedToSave'));
    }
  };

  const filterDefs = [
    {
      key: 'dropshippingEnabled',
      label: t('dropshipping.dropshippingAllowed'),
      type: 'select',
      options: [
        { value: '', label: t('common.all') },
        { value: 'true', label: t('common.enabled') },
        { value: 'false', label: t('common.disabled') },
      ],
    },
  ];

  const columns = [
    {
      key: 'productName',
      header: t('dropshipping.product'),
      sortable: true,
      render: (row) => (
        <div>
          <div className={styles.productName}>{row.productName}</div>
          {row.variationLabel && (
            <div className={styles.variationLabel}>{row.variationLabel}</div>
          )}
        </div>
      ),
    },
    {
      key: 'dropshippingEnabled',
      header: t('dropshipping.dropshippingAllowed'),
      align: 'center',
      width: '140px',
      render: (row) => (
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={!!getRowValue(row, 'dropshippingEnabled')}
            onChange={(e) => handleEdit(row, 'dropshippingEnabled', e.target.checked)}
          />
          <span className={styles.toggleSlider} />
        </label>
      ),
    },
    {
      key: 'dropshippingPrice',
      header: t('dropshipping.dropshippingPrice'),
      width: '140px',
      render: (row) => (
        <input
          type="number"
          min="0"
          step="0.01"
          className={styles.priceInput}
          value={getRowValue(row, 'dropshippingPrice') ?? ''}
          placeholder="0.00"
          onChange={(e) => handleEdit(row, 'dropshippingPrice', e.target.value)}
        />
      ),
    },
    {
      key: 'minimumSellingPrice',
      header: t('dropshipping.minSellingPrice'),
      width: '140px',
      render: (row) => (
        <input
          type="number"
          min="0"
          step="0.01"
          className={styles.priceInput}
          value={getRowValue(row, 'minimumSellingPrice') ?? ''}
          placeholder="0.00"
          onChange={(e) => handleEdit(row, 'minimumSellingPrice', e.target.value)}
        />
      ),
    },
    {
      key: 'maximumSellingPrice',
      header: t('dropshipping.maxSellingPrice'),
      width: '140px',
      render: (row) => (
        <input
          type="number"
          min="0"
          step="0.01"
          className={styles.priceInput}
          value={getRowValue(row, 'maximumSellingPrice') ?? ''}
          placeholder="0.00"
          onChange={(e) => handleEdit(row, 'maximumSellingPrice', e.target.value)}
        />
      ),
    },
  ];

  const actions = {
    width: '80px',
    frozen: true,
    render: (row) => {
      const key = getRowKey(row);
      const hasPending = !!pendingEdits[key];
      const isSaving = !!savingRows[key];
      return (
        <button
          className={styles.saveBtn}
          onClick={() => handleSave(row)}
          disabled={!hasPending || isSaving}
          title={t('dropshipping.saveRow')}
        >
          <Save size={14} />
          {isSaving ? '...' : t('dropshipping.saveRow')}
        </button>
      );
    },
  };

  return (
    <div className={styles.container}>
      <FilterPanel
        filters={filterDefs}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
        show={showFilters}
        onToggle={() => setShowFilters((v) => !v)}
        onExport={handleExport}
        showExport
        exportButtonText={t('common.export')}
        searchConfig={{
          show: true,
          value: search,
          onChange: handleSearchChange,
          placeholder: t('dropshipping.searchProducts'),
        }}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: (size) => handlePaginationChange({ ...pagination, pageSize: size, currentPage: 1 }),
          options: pagination.pageSizeOptions,
        }}
      />
      <DataTable
        data={data}
        columns={columns}
        actions={actions}
        loading={loading}
        sorting={sorting}
        onSortChange={handleSortChange}
        pagination={{ ...pagination }}
        onPaginationChange={handlePaginationChange}
        emptyMessage={t('dropshipping.noProviderProducts')}
        emptyDescription={t('dropshipping.noProviderProductsDescription')}
        stickyHeader
      />
    </div>
  );
};

export default ProviderProductsTab;
